import React from "react";
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  ChartBox, CHART_MARGIN, GRID, xAxisProps, yAxisProps,
  compactAxis, num, renderRefLines, SUCCESS, DANGER,
} from "./_kit";
import { PathwayTip } from "./recharts.styles";

const BRAND = "#3f88a5";
const GUIDE = "#7a6ba8";

const trackStatus = (actual, required) => {
  if (actual == null || required == null) return null;
  const onTrack = actual <= required;
  return { onTrack, pct: required > 0 ? Math.abs((actual - required) / required) * 100 : null };
};

function StatusLine({ actual, required, style }) {
  const s = trackStatus(actual, required);
  if (!s) return null;
  return (
    <div style={{ marginTop: 3, fontWeight: 700, color: s.onTrack ? SUCCESS : DANGER, ...style }}>
      {s.onTrack ? "✓ On track" : "✗ Off track"}
      {s.pct != null && <span style={{ fontWeight: 600 }}>{` — ${s.pct.toFixed(1)}%`}</span>}
    </div>
  );
}

function PathwayTooltip({ active, payload, label, overlays = [] }) {
  if (!active || !payload?.length) return null;
  const req = payload.find((p) => p.dataKey === "required");
  const act = payload.find((p) => p.dataKey === "actual");
  const plan = payload.find((p) => p.dataKey === "onTrack");
  const row = payload[0]?.payload || {};
  return (
    <PathwayTip>
      <div className="yr">{label}</div>
      {req && req.value != null && <div className="req">Required: {num(req.value)} tCO₂e</div>}
      {act && act.value != null && <div className="act">Actual: {num(act.value)} tCO₂e</div>}
      {act && req && <StatusLine actual={act.value} required={req.value} />}
      {plan && plan.value != null && (!act || act.value == null) && (
        <div className="plan">On-track path: {num(plan.value)} tCO₂e</div>
      )}
      {overlays.map((o) => (row[o.key] != null
        ? <div key={o.key} style={{ color: o.color, fontSize: 11.5 }}>{o.name}: {num(row[o.key])} tCO₂e</div>
        : null))}
    </PathwayTip>
  );
}

/**
 * NetZero required-vs-actual pathway: required budget as a filled area, actual as
 * a line, an on-track guide line, and green/red shading for the gap, with
 * near-term / net-zero reference lines. Reused by both the per-scope panel and
 * the combined horizon dashboard (which passes precomputed onTrack + overlays).
 * @param {{year:number,required?:number,actual?:number,onTrack?:number}[]} props.rows
 * @param {number} [props.nearTermYear] - draws a near-term reference line
 * @param {number} [props.netZeroYear] - draws a net-zero reference line
 * @param {{fromYear:number,fromValue:number,ratePct:number,netZeroYear:number}} [props.plan] - derives onTrack when a row has none
 * @param {{key:string,name:string,color:string}[]} [props.overlays] - extra actual lines (e.g. per-scope)
 * @param {number} [props.height=320]
 * @param {number} [props.xInterval] - x-axis tick interval (recharts auto when omitted)
 */
export function PathwayChart({ rows, nearTermYear, netZeroYear, plan, overlays = [], height = 320, xInterval }) {
  const chartData = rows.map((r) => {
    const required = r.required == null ? null : Number(r.required);
    const actual = r.actual == null ? null : Number(r.actual);
    const hasActual = actual != null && required != null;
    const over = hasActual && actual > required ? actual - required : 0;
    const under = hasActual && actual < required ? required - actual : 0;
    let onTrack = r.onTrack ?? null;
    if (onTrack == null && plan && Number(r.year) >= plan.fromYear && Number(r.year) <= plan.netZeroYear) {
      onTrack = plan.fromValue * Math.pow(1 - plan.ratePct / 100, Number(r.year) - plan.fromYear);
    }
    const point = {
      year: r.year, required, actual, onTrack,
      obBase: required ?? 0, obOver: over,
      ubBase: hasActual ? actual : 0, ubUnder: under,
    };
    overlays.forEach((o) => { point[o.key] = r[o.key] ?? null; });
    return point;
  });

  const refLines = [
    nearTermYear && { x: nearTermYear, label: `Near-term ${nearTermYear}` },
    netZeroYear && { x: netZeroYear, label: `Net-zero ${netZeroYear}` },
  ].filter(Boolean);

  return (
    <ChartBox $height={height}>
      <ResponsiveContainer>
        <ComposedChart data={chartData} margin={CHART_MARGIN}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="year" {...xAxisProps} interval={xInterval} />
          <YAxis {...yAxisProps} tickFormatter={compactAxis} />
          <Tooltip content={<PathwayTooltip overlays={overlays} />} />

          <Area type="monotone" dataKey="required" stroke={BRAND} strokeWidth={2} fill={BRAND} fillOpacity={0.08} dot={false} />

          <Area type="monotone" dataKey="ubBase" stackId="ub" stroke="none" fill="none" fillOpacity={0} activeDot={false} isAnimationActive={false} />
          <Area type="monotone" dataKey="ubUnder" stackId="ub" stroke="none" fill={SUCCESS} fillOpacity={0.16} activeDot={false} isAnimationActive={false} />

          <Area type="monotone" dataKey="obBase" stackId="ob" stroke="none" fill="none" fillOpacity={0} activeDot={false} isAnimationActive={false} />
          <Area type="monotone" dataKey="obOver" stackId="ob" stroke="none" fill={DANGER} fillOpacity={0.14} activeDot={false} isAnimationActive={false} />

          {renderRefLines(refLines)}

          <Line type="monotone" dataKey="onTrack" stroke={GUIDE} strokeWidth={2} strokeDasharray="6 5" dot={false} connectNulls={false} />
          <Line type="monotone" dataKey="actual" stroke={DANGER} strokeWidth={2} dot={{ r: 4, fill: DANGER, strokeWidth: 2, stroke: "#fff" }} connectNulls={false} />

          {overlays.map((o) => (
            <Line key={o.key} type="monotone" dataKey={o.key} name={o.name} stroke={o.color} strokeWidth={1.5} dot={false} connectNulls opacity={0.6} isAnimationActive={false} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartBox>
  );
}
